import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import { format, isValid, parse } from "date-fns";
import { enqueueSnackbar } from "notistack";
import {
  archivePartnerCustomer,
  archivePartnerTaskOrder,
  listPartnerTaskOrders,
} from "../../helpers/partnerCentral_helper";
import {
  AH_ACCENT,
  AH_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_WHITE,
  tableShellSx,
} from "../Compliance/AuditHub/theme";
import TaskOrdersDialog from "./TaskOrdersDialog";

const ADD_CUSTOMER_PATH = "/partner-central/customer/add-customer";
const ADD_TASK_ORDER_PATH = "/partner-central/customer/add-task-order";

const HEAD_CELL_SX = {
  fontSize: 12,
  fontWeight: 600,
  color: AH_MUTED,
  whiteSpace: "nowrap",
  borderColor: AH_BORDER,
  backgroundColor: "#f8fafc",
};

const BODY_CELL_SX = {
  fontSize: 13,
  color: AH_TEXT,
  borderColor: AH_BORDER,
  verticalAlign: "middle",
};

const formatDate = (value) => {
  if (!value) return "—";
  const parsed = parse(String(value).slice(0, 10), "yyyy-MM-dd", new Date());
  return isValid(parsed) ? format(parsed, "MM/dd/yyyy") : String(value);
};

const formatCreatedAt = (value) => {
  if (!value) return "—";
  const normalized = String(value).includes("T") ? value : String(value).replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return String(value);
  return format(date, "MM/dd/yyyy");
};

const statusChipSx = (status) => {
  if (status === "ACTIVE") {
    return {
      height: 24,
      fontSize: 12,
      fontWeight: 600,
      color: "#166534",
      backgroundColor: "#dcfce7",
      border: "1px solid #bbf7d0",
    };
  }
  if (status === "DRAFT") {
    return {
      height: 24,
      fontSize: 12,
      fontWeight: 600,
      color: "#92400e",
      backgroundColor: "#ffedd5",
      border: `1px solid ${AH_ACCENT}55`,
    };
  }
  return {
    height: 24,
    fontSize: 12,
    fontWeight: 600,
    color: AH_MUTED,
    backgroundColor: "#f3f4f6",
    border: `1px solid ${AH_BORDER}`,
  };
};

const CustomersGrid = ({ customers, loading, error, onRefresh }) => {
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [counts, setCounts] = useState({});
  const [countsLoading, setCountsLoading] = useState(false);
  const [dialogCustomer, setDialogCustomer] = useState(null);
  const [dialogOrders, setDialogOrders] = useState([]);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [deletingCustomerId, setDeletingCustomerId] = useState(null);
  const [ordersVersion, setOrdersVersion] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [customers]);

  useEffect(() => {
    if (!customers.length) {
      setCounts({});
      return undefined;
    }
    let cancelled = false;
    setCountsLoading(true);
    listPartnerTaskOrders({ statuses: ["DRAFT", "ACTIVE"] })
      .then((items) => {
        if (cancelled) return;
        const next = {};
        items.forEach((order) => {
          // Task Orders key off real organization_id, not commercial profile id.
          const key = String(order.associated_organization_id ?? "");
          if (!key) return;
          next[key] = (next[key] || 0) + 1;
        });
        setCounts(next);
      })
      .catch(() => {
        if (!cancelled) setCounts({});
      })
      .finally(() => {
        if (!cancelled) setCountsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customers, ordersVersion]);

  useEffect(() => {
    if (!dialogCustomer?.id) {
      setDialogOrders([]);
      setDialogError("");
      return undefined;
    }
    let cancelled = false;
    setDialogLoading(true);
    setDialogError("");
    listPartnerTaskOrders({
      associatedOrganizationId: dialogCustomer.organization_id ?? dialogCustomer.id,
      statuses: ["DRAFT", "ACTIVE"],
    })
      .then((items) => {
        if (!cancelled) setDialogOrders(items);
      })
      .catch((err) => {
        if (!cancelled) {
          setDialogOrders([]);
          setDialogError(err?.message || "Unable to load workorders.");
        }
      })
      .finally(() => {
        if (!cancelled) setDialogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dialogCustomer, ordersVersion]);

  const rows = useMemo(() => {
    const start = page * rowsPerPage;
    return customers.slice(start, start + rowsPerPage);
  }, [customers, page, rowsPerPage]);

  const openEditCustomer = (customer) => {
    if (!customer?.id) return;
    history.push(`${ADD_CUSTOMER_PATH}?id=${encodeURIComponent(customer.id)}`);
  };

  const handleDeleteCustomer = async (customer) => {
    if (!customer?.id || deletingCustomerId) return;
    const label = customer.organization_name || customer.customer_code || "this customer";
    if (
      !window.confirm(
        `Archive ${label}? This removes it from the active customers list. Customers with active workorders cannot be archived.`
      )
    ) {
      return;
    }
    setDeletingCustomerId(customer.id);
    try {
      await archivePartnerCustomer(customer.id);
      enqueueSnackbar(`Customer archived: ${label}.`, { variant: "success" });
      if (typeof onRefresh === "function") onRefresh();
    } catch (err) {
      enqueueSnackbar(err?.message || "Unable to archive customer.", { variant: "error" });
    } finally {
      setDeletingCustomerId(null);
    }
  };

  const openCreateTaskOrder = (customer) => {
    history.push(`${ADD_TASK_ORDER_PATH}?customerId=${encodeURIComponent(customer.id)}`);
  };

  const openEditTaskOrder = (order) => {
    if (!dialogCustomer?.id || !order?.id) return;
    setDialogCustomer(null);
    history.push(
      `${ADD_TASK_ORDER_PATH}?customerId=${encodeURIComponent(dialogCustomer.id)}&id=${encodeURIComponent(order.id)}`
    );
  };

  const handleDeleteTaskOrder = async (order) => {
    if (!order?.id || deletingId) return;
    const label = order.task_order_number || "this workorder";
    setDeletingId(order.id);
    try {
      await archivePartnerTaskOrder(order.id);
      enqueueSnackbar(`Workorder archived: ${label}.`, { variant: "success" });
      setOrdersVersion((value) => value + 1);
    } catch (err) {
      enqueueSnackbar(err?.message || "Unable to archive workorder.", { variant: "error" });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box sx={{ ...tableShellSx, mt: 2.5 }}>
      <Typography
        component="h2"
        sx={{
          m: 0,
          mb: 1.5,
          fontWeight: 700,
          fontSize: "0.9375rem",
          color: AH_TEXT,
          letterSpacing: "-0.01em",
        }}
      >
        Customers
      </Typography>

      {error ? (
        <Typography sx={{ fontSize: 13, color: "#d32f2f", py: 2 }} role="alert">
          {error}
        </Typography>
      ) : null}

      <TableContainer
        sx={{
          border: `1px solid ${AH_BORDER}`,
          borderRadius: "8px",
          backgroundColor: AH_WHITE,
          overflowX: "auto",
        }}
      >
        <Table size="small" aria-label="Partner Central customers">
          <TableHead>
            <TableRow>
              {[
                "Organization",
                "Customer ID",
                "Status",
                "Contact",
                "Contract Start",
                "Contract End",
                "Created",
                "Task Orders",
                "Actions",
              ].map((heading) => (
                <TableCell key={heading} sx={HEAD_CELL_SX}>
                  {heading}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ ...BODY_CELL_SX, py: 5, borderBottom: "none" }}>
                  <CircularProgress size={28} sx={{ color: AH_ACCENT }} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ ...BODY_CELL_SX, py: 4, color: AH_MUTED, borderBottom: "none" }}>
                  No draft or active customers yet.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((customer) => {
                const organizationKey = String(
                  customer.organization_id ?? customer.id ?? ""
                );
                const count = counts[organizationKey] || 0;
                return (
                  <TableRow key={customer.id} hover>
                    <TableCell sx={{ ...BODY_CELL_SX, fontWeight: 500, minWidth: 180 }}>
                      {customer.organization_name || "—"}
                    </TableCell>
                    <TableCell sx={{ ...BODY_CELL_SX, minWidth: 120 }}>
                      {customer.customer_code || "—"}
                    </TableCell>
                    <TableCell sx={BODY_CELL_SX}>
                      <Chip
                        size="small"
                        label={customer.status || "—"}
                        sx={statusChipSx(customer.status)}
                      />
                    </TableCell>
                    <TableCell sx={{ ...BODY_CELL_SX, minWidth: 160 }}>
                      <Typography sx={{ fontSize: 13, color: AH_TEXT }}>
                        {customer.primary_contact_name || "—"}
                      </Typography>
                      {customer.primary_contact_email ? (
                        <Typography sx={{ fontSize: 12, color: AH_MUTED, mt: 0.25 }}>
                          {customer.primary_contact_email}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={BODY_CELL_SX}>{formatDate(customer.contract_start_date)}</TableCell>
                    <TableCell sx={BODY_CELL_SX}>{formatDate(customer.contract_end_date)}</TableCell>
                    <TableCell sx={BODY_CELL_SX}>{formatCreatedAt(customer.created_at)}</TableCell>
                    <TableCell sx={BODY_CELL_SX}>
                      {countsLoading ? (
                        <CircularProgress size={16} sx={{ color: AH_MUTED }} />
                      ) : (
                        <Link
                          component="button"
                          type="button"
                          underline="hover"
                          onClick={() => setDialogCustomer(customer)}
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: AH_ACCENT,
                            cursor: "pointer",
                            border: "none",
                            background: "transparent",
                            p: 0,
                          }}
                        >
                          {count}
                        </Link>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...BODY_CELL_SX, width: 128, whiteSpace: "nowrap" }}>
                      <IconButton
                        size="small"
                        aria-label={`Edit ${customer.organization_name || "customer"}`}
                        onClick={() => openEditCustomer(customer)}
                        sx={{ color: AH_MUTED, "&:hover": { color: AH_ACCENT } }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={`Delete ${customer.organization_name || "customer"}`}
                        disabled={deletingCustomerId === customer.id}
                        onClick={() => handleDeleteCustomer(customer)}
                        sx={{ color: AH_MUTED, "&:hover": { color: AH_ACCENT } }}
                      >
                        {deletingCustomerId === customer.id ? (
                          <CircularProgress size={16} sx={{ color: AH_MUTED }} />
                        ) : (
                          <DeleteOutlineIcon fontSize="small" />
                        )}
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={`Create task order for ${customer.organization_name || customer.customer_code || "customer"}`}
                        onClick={() => openCreateTaskOrder(customer)}
                        sx={{ color: AH_MUTED, "&:hover": { color: AH_ACCENT } }}
                      >
                        <NoteAddOutlinedIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && !error && customers.length > 0 ? (
        <TablePagination
          component="div"
          count={customers.length}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
          sx={{
            mt: 0.5,
            color: AH_MUTED,
            ".MuiTablePagination-displayedRows, .MuiTablePagination-selectLabel": {
              fontSize: 13,
            },
          }}
        />
      ) : null}

      <TaskOrdersDialog
        open={Boolean(dialogCustomer)}
        onClose={() => setDialogCustomer(null)}
        customer={dialogCustomer}
        taskOrders={dialogOrders}
        loading={dialogLoading}
        error={dialogError}
        onEdit={openEditTaskOrder}
        onDelete={handleDeleteTaskOrder}
        deletingId={deletingId}
      />
    </Box>
  );
};

export default CustomersGrid;
